function getSelectedValues(field) {
    const value = field?.value;
    if (Array.isArray(value)) return value;
    return value == null || value === '' ? [] : [value];
}

function getNAValues(options) {
    return new Set(
        (Array.isArray(options) ? options : [])
            .filter((option) => option?.isNA === true)
            .map((option) => option.value)
    );
}

/**
 * N/A is template metadata, not a display label. Any selected option marked
 * `isNA: true` makes the field not applicable.
 */
export function isAuditFieldNotApplicable(field) {
    const naValues = getNAValues(field?.params?.options);
    return getSelectedValues(field).some((value) => naValues.has(value));
}

export function hasMixedAuditNASelection(field) {
    const selectedValues = getSelectedValues(field);
    if (selectedValues.length < 2) return false;

    const naValues = getNAValues(field?.params?.options);
    return (
        selectedValues.some((value) => naValues.has(value)) &&
        selectedValues.some((value) => !naValues.has(value))
    );
}

/**
 * Keeps N/A mutually exclusive in checkbox groups.
 *
 * Selecting N/A clears applicable choices. Selecting an applicable choice
 * while N/A is selected clears N/A. Malformed mixed input defaults to N/A.
 */
export function normalizeExclusiveNASelection({
    previousValues = [],
    nextValues = [],
    options = [],
}) {
    const previous = Array.isArray(previousValues) ? previousValues : [];
    const next = Array.isArray(nextValues) ? nextValues : [];
    const naValues = getNAValues(options);
    const selectedNA = next.filter((value) => naValues.has(value));
    const selectedApplicable = next.filter((value) => !naValues.has(value));

    if (!selectedNA.length || !selectedApplicable.length) {
        return next;
    }

    const previouslyHadNA = previous.some((value) => naValues.has(value));
    if (previouslyHadNA) {
        return selectedApplicable;
    }

    return selectedNA.slice(0, 1);
}

/**
 * Normalizes persisted checkbox values defensively. Historical or imported
 * mixed selections use the conservative N/A-wins rule.
 */
export function normalizeAuditFieldNAValue(field) {
    if (field?.type !== 'checkbox-group' || !hasMixedAuditNASelection(field)) {
        return field?.value;
    }

    const naValues = getNAValues(field?.params?.options);
    return getSelectedValues(field)
        .filter((value) => naValues.has(value))
        .slice(0, 1);
}
